import type { Task } from '@ztechapps/common.contract'

export enum TaskBoardGroupByEnum {
  None = 'none',
  Project = 'project',
  Assignee = 'assignee',
  WorkOrder = 'workOrder',
}

export type GroupedSection = {
  key: string
  label: string
  tasks: Task[]
}

type GroupKeyLabel = { key: string; label: string }

export const getGroupKeyLabel = (task: Task, groupByValue: TaskBoardGroupByEnum): GroupKeyLabel => {
  if (groupByValue === TaskBoardGroupByEnum.Project) {
    const code = task.project?.code
    const name = task.project?.name
    if (!code) return { key: 'No Project', label: 'No Project' }
    return { key: code, label: name ? `${code} - ${name}` : code }
  }
  if (groupByValue === TaskBoardGroupByEnum.Assignee) {
    const name = task.members?.[0]?.profile?.name ?? 'Unassigned'
    return { key: name, label: name }
  }
  if (groupByValue === TaskBoardGroupByEnum.WorkOrder) {
    const woCode = task.workOrder?.code
    const productCode = task.workOrder?.product?.code
    if (!woCode) return { key: 'No Production', label: 'No Production' }
    return { key: woCode, label: productCode ? `${woCode} - ${productCode}` : woCode }
  }
  return { key: 'all', label: 'All' }
}

/** @deprecated Use getGroupKeyLabel instead */
export const getGroupKey = (task: Task, groupByValue: TaskBoardGroupByEnum): string => {
  return getGroupKeyLabel(task, groupByValue).key
}

export const groupTasksByStatus = (tasks: Task[]): Record<string, Task[]> => {
  const map: Record<string, Task[]> = {
    Draft: [],
    'In Progress': [],
    Complete: [],
    Cancelled: [],
  }
  for (const task of tasks) {
    const bucket = map[task.status]
    if (bucket) {
      bucket.push(task)
    }
  }
  return map
}

export const buildTaskGroups = (tasks: Task[], groupByValue: TaskBoardGroupByEnum): GroupedSection[] => {
  if (groupByValue === TaskBoardGroupByEnum.None) return [{ key: 'all', label: 'All', tasks }]

  const grouped: Record<string, { label: string; tasks: Task[] }> = {}
  for (const task of tasks) {
    const { key, label } = getGroupKeyLabel(task, groupByValue)
    if (!grouped[key]) grouped[key] = { label, tasks: [] }
    grouped[key].tasks.push(task)
  }

  return Object.entries(grouped)
    .map(([key, group]) => ({ key, label: group.label, tasks: group.tasks }))
    .sort((a, b) => a.label.localeCompare(b.label))
}
